<template>
    <teleport to="body">
        <div v-if="show" class="pm-overlay" @keydown.esc="onClose()">
            <div class="pm-modal" role="dialog" :aria-label="title">
                <header class="pm-header">
                    <h3>{{ title }}</h3>
                    <button class="pm-close" @click="onClose()">✕</button>
                </header>

                <input class="pm-search" type="text" v-model="q" :placeholder="placeholder" autofocus />

                <div v-if="activeFilters.length" class="pm-active-filters">
                    <span v-for="filter in activeFilters" :key="filter.key" class="pm-filter-tag">
                        {{ filter.label }}
                        <button class="pm-filter-tag-x" @click="removeFilter(filter.key)"
                            aria-label="Remove filter">×</button>
                    </span>
                </div>
                <div class="pm-filters">
                    <select class="pm-filter" v-model="filters.age">
                        <option value="">Age</option>
                        <option v-for="a in optionsAge" :key="a" :value="a">{{ a }}</option>
                    </select>
                    <select class="pm-filter" v-model="filters.gender">
                        <option value="">Gender</option>
                        <option v-for="g in optionsGender" :key="g" :value="g">{{ g }}</option>
                    </select>
                    <select class="pm-filter" v-model="filters.accent">
                        <option value="">Accent</option>
                        <option v-for="ac in optionsAccent" :key="ac" :value="ac">{{ ac }}</option>
                    </select>
                    <select class="pm-filter" v-model="filters.quality">
                        <option value="">Voice Quality</option>
                        <option v-for="qv in optionsQualityDisplay" :key="qv.value" :value="qv.value">
                            {{ qv.label }}</option>
                    </select>
                </div>
                <div class="pm-gallery-toggle">
                    <label class="switch">
                        <input type="checkbox" v-model="onlyMyGallery" />
                        <span class="slider"></span>
                        <span class="switch-label">
                            Only My Gallery
                        </span>
                    </label>
                </div>
                <div class="pm-list">
                    <div v-for="item in filtered" :key="itemKey(item)" class="pm-item vc-card" tabindex="0"
                        @click="select(item)">
                        <div class="vc-avatar" @click.stop="play(item)" :class="{ disabled: isLoading(item) }">
                            <img v-if="voiceImageUrls[item.voice_name]" :src="voiceImageUrls[item.voice_name]"
                                alt="Profile pic" />
                            <span v-if="!isLoading(item)" class="play-icon">▶</span>
                            <span v-if="isLoading(item)" class="spinner"></span>
                        </div>
                        <div class="vc-content">
                            <div class="vc-header">
                                <div class="vc-name-tags">
                                    <h3>{{ displayName(item) }}</h3>
                                    <div class="vc-tags">
                                        <span v-if="item.accent">{{ item.accent }}</span>
                                        <span v-if="item.gender"> · {{ item.gender }}</span>
                                        <span v-if="ageLabel(item)"> · {{ ageLabel(item) }}</span>
                                    </div>
                                </div>
                                <div class="vc-btn-group">

                                    <button class="vc-fav-btn" :class="{ active: isInGallery(item) }"
                                        @click.stop="toggleMyGallery(item)"
                                        :aria-label="isInGallery(item) ? 'Remove from My Gallery' : 'Add to My Gallery'">
                                        ♥
                                    </button>
                                </div>
                            </div>
                            <p class="vc-description">{{ descText(item) }}</p>
                        </div>
                    </div>
                    <div v-if="filtered.length === 0" class="pm-empty">There is no voice here, only the sound of
                        silence.
                    </div>
                </div>

                <footer class="pm-actions">
                    <button class="pm-cancel" @click="onClose()">Cancel</button>
                </footer>
            </div>
        </div>
    </teleport>
</template>

<script>
import { defineComponent, ref, computed, reactive, onMounted, watch } from 'vue';
import { patchCollectVoice, deleteCollectVoice, getCollectVoiceIds } from '@/api/myGallery'
import { downloadDemoFile, getVoiceProfilePicUrl } from '@/api/files';
import { formatVoiceDisplayName } from '@/utils/voiceDisplayName';

export default defineComponent({
    name: 'VoicePickerModal',
    props: {
        show: { type: Boolean, default: false },
        items: { type: Array, default: () => [] },
        title: { type: String, default: 'Select' },
        placeholder: { type: String, default: 'Search…' },
        labelKey: { type: String, default: 'label' },
        subLabelKey: { type: String, default: '' },
        // Keys to search in; fallback to labelKey
        searchKeys: { type: Array, default: () => [] }
    },
    emits: ['update:show', 'select'],
    setup(props, { emit }) {
        const q = ref('');
        const onlyMyGallery = ref(false);
        const myGalleryIds = ref([]);
        const loadingVoices = ref(new Set());
        const voiceImageUrls = reactive({});
        const currentAudio = ref(null);
        const KOKORO_MODELS = ['Kokoro v1.0', 'Kokoro_v1.0', 'Kokoro v1_0', 'Kokoro_v1'];
        const searchFields = computed(() =>
            props.searchKeys.length ? props.searchKeys : [props.labelKey]
        );
        const filters = reactive({ age: '', gender: '', accent: '', quality: '' });

        const AGE_BUCKETS = [
            { label: 'Young adult', min: 0, max: 30 },
            { label: 'Middle-aged', min: 31, max: 60 },
            { label: 'Older adult', min: 61, max: Infinity }
        ];
        const optionsAge = computed(() => AGE_BUCKETS.map(b => b.label));
        const optionsGender = computed(() => {
            const vals = props.items.map(i => i.gender).filter(Boolean);
            return Array.from(new Set(vals));
        });
        const optionsAccent = computed(() => {
            const vals = props.items.map(i => i.accent).filter(Boolean);
            return Array.from(new Set(vals));
        });
        const optionsQuality = computed(() => {
            const set = new Set();
            props.items.forEach(i => {
                if (Array.isArray(i.voice_quality)) i.voice_quality.forEach(q => set.add(q));
            });
            return Array.from(set).sort();
        });

        const optionsQualityDisplay = computed(() =>
            optionsQuality.value.map(q => ({
                label: q.charAt(0).toUpperCase() + q.slice(1),
                value: q
            }))
        );
        const filtered = computed(() => {
            const raw = q.value || '';
            // Split by "||" and trim; ignore empty pieces
            const terms = raw
                .split('||')
                .map(t => t.trim().toLowerCase())
                .filter(Boolean);

            let base = props.items;

            // If toggled, restrict to My Gallery voices
            if (onlyMyGallery.value) {
                base = base.filter(it => {
                    const id = it.voice_id ?? it.id ?? it.voiceId ?? '';
                    return myGalleryIds.value.includes(String(id));
                });
            }

            // Text search (OR across terms, OR across fields)
            if (terms.length) {
                base = base.filter(it =>
                    terms.some(term =>
                        searchFields.value.some(k => {
                            const v = (it?.[k] ?? '').toString().toLowerCase();
                            return v.includes(term);
                        })
                    )
                );
            }

            // Age filter
            if (filters.age) {
                const bucket = AGE_BUCKETS.find(b => b.label === filters.age);
                if (bucket) {
                    base = base.filter(it => {
                        const a = Number(it.age);
                        if (Number.isNaN(a)) return false;
                        return a >= bucket.min && a <= bucket.max;
                    });
                }
            }

            // Gender filter
            if (filters.gender) base = base.filter(it => it.gender === filters.gender);

            // Accent filter
            if (filters.accent) base = base.filter(it => it.accent === filters.accent);

            // Voice quality filter
            if (filters.quality)
                base = base.filter(
                    it => Array.isArray(it.voice_quality) && it.voice_quality.includes(filters.quality)
                );

            return base;
        });

        const itemKey = it => it.id ?? it[props.labelKey] ?? JSON.stringify(it);

        // Active filters for tag display
        const activeFilters = computed(() => {
            const tags = [];
            if (filters.age) tags.push({ key: 'age', label: `Age: ${filters.age}` });
            if (filters.gender) tags.push({ key: 'gender', label: `Gender: ${filters.gender}` });
            if (filters.accent) tags.push({ key: 'accent', label: `Accent: ${filters.accent}` });
            if (filters.quality) {
                // Find display label for quality
                const found = optionsQualityDisplay.value.find(q => q.value === filters.quality);
                tags.push({ key: 'quality', label: `Voice Quality: ${found ? found.label : filters.quality}` });
            }
            return tags;
        });
        function removeFilter(key) {
            filters[key] = '';
        }
        function descText(item) {
            if (item.description && item.description.trim() !== '') return item.description;
            const q = item?.voice_quality;
            if (Array.isArray(q) && q.length) {
                const sentence = `The ${q.slice(0, 5).join(', ')} voice${q.length > 5 ? '…' : ''}.`;
                return sentence.replace(' ,', ',');
            }
            return '';
        }

        function displayName(item) {
            const raw = item.voice_name ?? item[props.labelKey] ?? item.name ?? item.id ?? '';
            const modelName = item.model ?? item.model_name ?? '';
            return formatVoiceDisplayName(raw, modelName);
        }

        function ageLabel(item) {
            const a = Number(item.age);
            if (Number.isNaN(a)) return '';
            const bucket = AGE_BUCKETS.find(b => a >= b.min && a <= b.max);
            return bucket ? bucket.label : '';
        }

        function onClose() {
            if (currentAudio.value) {
                currentAudio.value.pause();
                currentAudio.value = null;
            }
            emit('update:show', false);
        }
        function select(item) {
            if (currentAudio.value) {
                currentAudio.value.pause();
                currentAudio.value = null;
            }
            emit('select', item);
            emit('update:show', false);
            q.value = '';
        }

        async function play(item) {
            if (currentAudio.value) {
                currentAudio.value.pause();
                currentAudio.value = null;
            }
            const modelName = item.model ?? item.model_name ?? '';
            if (!KOKORO_MODELS.includes(modelName)) return;

            const key = voiceKey(item);
            loadingVoices.value.add(key);

            try {
                const data = await downloadDemoFile('Kokoro v1.0', item.voice_name);
                const wavBlob = new Blob([data], { type: 'audio/wav' });
                const audioUrl = URL.createObjectURL(wavBlob);

                const audio = new Audio(audioUrl);
                audio.play();
                currentAudio.value = audio;
                audio.onended = () => URL.revokeObjectURL(audioUrl);
            } catch (err) {
                console.error('Download demo voice error: ', err);
            }

            loadingVoices.value.delete(key);
        }
        function voiceKey(item) {
            return item.voice_name ?? item[props.labelKey] ?? item.id ?? '';
        }

        function isLoading(item) {
            return loadingVoices.value.has(voiceKey(item));
        }

        function isInGallery(item) {
            const id = item.voice_id ?? item.id ?? item.voiceId ?? '';
            return myGalleryIds.value.includes(String(id));
        }

        async function toggleMyGallery(item) {
            const id = item.voice_id ?? item.id ?? item.voiceId ?? '';
            if (!id) {
                console.warn('toggleMyGallery: no voice_id on item', item);
                return;
            }

            const prev = [...myGalleryIds.value];
            const wasIn = myGalleryIds.value.includes(String(id));

            // optimistic UI update
            myGalleryIds.value = wasIn
                ? prev.filter(v => v !== id)
                : [...prev, id];

            try {
                if (wasIn) {
                    await deleteCollectVoice(id);
                } else {
                    await patchCollectVoice(id);
                }
            } catch (e) {
                // rollback on failure
                myGalleryIds.value = prev;
                console.error('toggleMyGallery failed', e);
            } finally {
                // background sync to ensure consistency
                refreshMyGallery().catch(() => { });
            }
        }

        async function refreshMyGallery() {
            try {
                const ids = await getCollectVoiceIds();
                myGalleryIds.value = Array.isArray(ids) ? ids : [];
            } catch (e) {
                console.error('refreshMyGallery failed', e);
            }
        }

        async function loadVoiceImages() {
            for (const item of props.items) {
                const name = item.voice_name ?? '';
                if (!name) continue;
                const imageUrl = getVoiceProfilePicUrl(item.model, name);
                if (imageUrl) voiceImageUrls[name] = imageUrl;
            }
        }
        watch(
            () => props.items,
            () => {
                Object.keys(voiceImageUrls).forEach(k => delete voiceImageUrls[k]);
                loadVoiceImages();
            },
            { immediate: true }
        );
        onMounted(async () => {
            refreshMyGallery();
        });
        return {
            KOKORO_MODELS,
            q,
            onlyMyGallery,
            myGalleryIds,
            filters,
            optionsAge,
            optionsGender,
            optionsAccent,
            optionsQuality,
            optionsQualityDisplay,
            filtered,
            searchFields,
            itemKey,
            descText,
            displayName,
            ageLabel,
            play,
            select,
            toggleMyGallery,
            isInGallery,
            voiceKey,
            loadingVoices,
            isLoading,
            voiceImageUrls,
            currentAudio,
            onClose,
            activeFilters,
            removeFilter,
        };
    }
});
</script>

<style scoped>
.pm-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, .45);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
}

.pm-modal {
    background: #fff;
    width: 50vw;
    height: 80vh;
    border-radius: 8px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, .2);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-sizing: border-box;
    max-width: 100%;
    max-height: 100%;
}

.pm-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: .75rem 1rem;
    border-bottom: 1px solid var(--border-color);
}

.pm-close {
    background: none;
    border: none;
    font-size: 1.2rem;
    cursor: pointer;
}

.pm-search {
    margin: .75rem 1rem;
    padding: .5rem .75rem;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    width: calc(100% - 2rem);
}


.pm-filters {
    margin-top: 1rem;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 12px;
    padding: 0 1rem 0.75rem 1rem;
    box-sizing: border-box;
}

.pm-filter {
    appearance: none;
    width: 100%;
    padding: 10px 14px;
    border: 1px solid var(--border-color);
    border-radius: 8px;
    font-size: 0.95rem;
    color: #1a2b4d;
    background: #fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23666' d='M6 8L0 0h12z'/%3E%3C/svg%3E") no-repeat right 12px center/12px 8px;
}

.pm-filter:focus {
    outline: none;
    border-color: var(--accent-indigo);
    box-shadow: 0 0 0 2px rgba(99, 102, 241, .15);
}

.pm-list {
    flex: 1 1 auto;
    overflow-y: auto;
    display: grid;
    grid-auto-rows: 120px;
    align-content: start;
}

.pm-item {
    padding: .6rem 1rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 100%;
    cursor: pointer;
    transition: background .15s;
    overflow: hidden;
}

.pm-item:hover {
    background: #f5f5f5;
}

.pm-empty {
    padding: 1rem;
    text-align: center;
    color: #888;
}

.pm-active-filters {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    padding: 0 1rem 0.5rem 1rem;
    align-items: center;
    min-height: 32px;
}

.pm-filter-tag {
    background: #f3eaff;
    color: var(--purple);
    border-radius: 16px;
    padding: 0.25rem 0.75rem 0.25rem 0.75rem;
    font-size: 0.92rem;
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
}

.pm-filter-tag-x {
    background: none;
    border: none;
    color: var(--purple);
    font-size: 1.1em;
    margin-left: 0.2em;
    cursor: pointer;
    padding: 0 0.2em;
    line-height: 1;
    border-radius: 50%;
    transition: background 0.15s;
}

.pm-filter-tag-x:hover {
    background: var(--purple-ultra-light);
    color: #6c3fc5;
}

.pm-actions {
    padding: .75rem 1rem;
    border-top: 1px solid var(--border-color);
    display: flex;
    justify-content: flex-end;
    gap: .5rem;
}

.pm-cancel {
    padding: 6px 14px;
    background: #ddd;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}

.vc-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 0.5rem;
}

.vc-name-tags h3 {
    font-size: 1.1rem;
}

.vc-card {
    background-color: #FDFDFF;
    border: 1px solid var(--border-color);
    transition: all 0.3s ease;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: .3rem;
    padding: 0.6rem 0.6rem 0.6rem 1.2rem;
    height: 100%;
    overflow: hidden;
}

.vc-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);
    border: white;
}


.vc-tags {
    font-size: 0.8rem;
    color: var(--accent-indigo);
    font-weight: 500;
}

.vc-description {
    font-size: 0.95rem;
    color: var(--text-secondary);
    margin-bottom: 0.5rem;
    line-height: 1.3;
}

.vc-header {
    position: relative;
    display: flex;
    align-items: center;
    gap: 1rem;
}

.vc-btn-group {
    margin-left: auto;
    display: flex;
    gap: 0.4rem;
    align-self: flex-start;
}

.vc-fav-btn {
    border: none;
    background: #ffe9ec;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    line-height: 32px;
    text-align: center;
    cursor: pointer;
    font-size: 16px;
    color: #ff4a6a;
    flex-shrink: 0;
    transition: background .15s;
}

.vc-fav-btn:hover {
    background: #ffdfe5;
}

.vc-fav-btn.active {
    background: #ff4a6a;
    color: #fff;
}

.pm-gallery-toggle {
    padding: 0 1rem 0.75rem 1rem;
    display: flex;
    justify-content: flex-end;
    box-sizing: border-box;
}

.pm-gallery-toggle .switch {
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    gap: 0.5rem;
    user-select: none;
    font-size: 0.9rem;
    color: #1a2b4d;
}

.pm-gallery-toggle .switch input {
    display: none;
}

.pm-gallery-toggle .switch .slider {
    position: relative;
    width: 44px;
    height: 24px;
    background: #d1d5db;
    border-radius: 12px;
    transition: background .2s;
    flex-shrink: 0;
}

.pm-gallery-toggle .switch .slider::after {
    content: "";
    position: absolute;
    top: 3px;
    left: 3px;
    width: 18px;
    height: 18px;
    background: #fff;
    border-radius: 50%;
    transition: transform .2s;
    box-shadow: 0 1px 3px rgba(0, 0, 0, .25);
}

.pm-gallery-toggle .switch input:checked+.slider {
    background: #ff9bb0;
}

.pm-gallery-toggle .switch input:checked+.slider::after {
    transform: translateX(20px);
}

.pm-gallery-toggle .switch-label {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    font-weight: 500;
    color: #4a4a6a;
}

.vc-avatar {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    margin-right: 12px;
    overflow: hidden;
    flex-shrink: 0;
    background: #f0f0f0;
    position: relative;
    cursor: pointer;
    transition: transform 0.2s, opacity 0.2s;
}

.vc-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.2s;
}

.vc-avatar.disabled {
    pointer-events: none;
    opacity: 0.6;
}

.vc-avatar:hover:not(.disabled) img {
    transform: scale(1.05);
}

.vc-avatar .spinner {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    border-width: 4px;
    border-style: solid;
    border-color: #4a61ff transparent transparent transparent;
    animation: spin 0.6s linear infinite;
    border-radius: 50%;
}

.vc-content {
    flex: 1;
    display: flex;
    flex-direction: column;
}

.play-icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 24px;
    color: rgba(255, 255, 255, 0.85);
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.2s ease;
}

.pm-item:hover .vc-avatar:not(.disabled) .play-icon {
    opacity: 1;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}
</style>
